import React, { useEffect, useState } from 'react';
import { Grid, Container } from '@material-ui/core';
import { 
  UserGroupIcon, 
  BuildingOfficeIcon,
  CurrencyDollarIcon 
} from '@heroicons/react/24/outline';
import ValuationTileContainer from '../../containers/ValuationTileContainer';
import ValueAccelerationContainer from '../../containers/ValueAccelerationContainer';
import DashboardTutorial from './ValueAcceleration/DashboardTutorial';
import BcpTileContainer from '../../containers/BcpTileContainer';
import CatastropheTileContainer from '../../containers/CatastropheTileContainer';
import SuccessionPlanningTileContainer from '../../containers/SuccessionPlanningTileContainer';
import PlanningProgress from './PlanningProgress';
import ActivityStream from './ActivityStream';
import ContingencyPlan from './ContingencyPlan';
import DealRoom from './DealRoom';
import MarketplaceMetrics from './MarketplaceMetrics';
import ErrorBoundary from '../ErrorBoundary';
import { ApolloProvider, useQuery, gql } from '@apollo/client';
import client from '../../lib/apolloClient';
import SuccessionDrawerWithListener from '../../containers/Succession/SuccessionDrawerWithListener';
import SellerValueMetricContainer from '../../containers/SellerValueMetricContainer';
import SuccessionThemeProvider, { useSuccessionTheme } from './SuccessionThemeProvider';
import { shouldShowUnifiedIntake, snoozeUnifiedIntakeForSession, shouldShowAssessmentPrompt } from '../../utils/unifiedIntake';
import AssessmentPromptEmptyState from '../Succession/AssessmentPromptEmptyState';
import AssessmentCtaTile from '../Succession/AssessmentCtaTile';
import ActionPlanCtaTile from '../Succession/ActionPlanCtaTile';
import { AgentProvider } from './AgentSystem/AgentProvider';
import AgentWidgetRenderer, { AgentTrigger } from './AgentSystem/AgentWidgetRenderer';
import AgentTriggerSystem from './AgentSystem/AgentTriggerSystem';
import AgentInsights from './AgentInsights';
import AIAssistantWidget from './AgentSystem/AIAssistantWidget';


// GraphQL query to fetch planning form data
const GET_PLANNING_STATUSES = gql`
  query GetPlanningStatuses {
    bcpFormDatum {
      id
      status
      completionPercentage
    }
    catastropheFormDatum {
      id
      status
      completionPercentage
    }
    successionPlanningFormDatum {
      id
      status
      completionPercentage
    }
  }
`;

// Inner component that uses the theme context
const GET_CURRENT_USER_MIN = gql`
  query GetCurrentUserMin {
    getCurrentUser {
      id
      features
      company { feature_types }
      triggering_events(limit: 50) { event_type completed_at }
    }
  }
`;

const SuccessionDashboardContent = ({ current_user, company, selected_user_name, agent_insights, agent_recommendations, show_agent_insights }) => {
  const { theme, isLightTheme, isDarkTheme } = useSuccessionTheme();
  const { data: cuData } = useQuery(GET_CURRENT_USER_MIN, { fetchPolicy: 'network-only', nextFetchPolicy: 'cache-first' });
  const [showIntakeOverlay, setShowIntakeOverlay] = useState(false);
  const [activeTab, setActiveTab] = useState('marketplace'); // 'value' | 'marketplace'
  const [showTutorial, setShowTutorial] = useState(false);
  const [vaRefreshToken, setVaRefreshToken] = useState(0);
  const [pendingTutorial, setPendingTutorial] = useState(false);

  // Fetch planning statuses with Apollo useQuery hook
  const { loading, error, data } = useQuery(GET_PLANNING_STATUSES);
  
  // Handle opening profile drawer when selected_user_name is provided
  useEffect(() => {
    if (selected_user_name) {
      console.log('SuccessionDashboard: Opening profile drawer for user:', selected_user_name);
      // Set document.x.a to trigger the profile drawer
      if (document.x) {
        document.x.a = selected_user_name;
      }
    }
  }, [selected_user_name]);
  
  // Determine step completion statuses based on BCP and Catastrophe data
  const getPlanningSteps = () => {
    const bcpStatus = data?.bcpFormDatum?.status || 'not_started';
    const catastropheStatus = data?.catastropheFormDatum?.status || 'not_started';
    const successionPlanningStatus = data?.successionPlanningFormDatum?.status || 'not_started';
    
    // Determine if steps are completed based on status
    const isBcpCompleted = bcpStatus === 'completed';
    const isCatastropheCompleted = catastropheStatus === 'completed';
    const isSuccessionPlanningCompleted = successionPlanningStatus === 'completed';
    
    return [
      { 
        label: "Continuity Planning", 
        completed: isBcpCompleted,
        completionPercentage: data?.bcpFormDatum?.completionPercentage || 0
      },
      { 
        label: "Catastrophic Planning", 
        completed: isCatastropheCompleted,
        completionPercentage: data?.catastropheFormDatum?.completionPercentage || 0
      },
      { 
        label: "Succession Planning", 
        completed: isSuccessionPlanningCompleted,
        completionPercentage: data?.successionPlanningFormDatum?.completionPercentage || 0
      }
    ];
  };

  // Theme-aware marketplace metrics data
  const activeBuyersValue = company?.marketplace_metrics?.active_buyers_value || 0;
  const activeBuyersChange = company?.marketplace_metrics?.active_buyers_change || 0;
  const followersCount = company?.marketplace_metrics?.followers_count || 0;
  const unreadMessagesCount = company?.marketplace_metrics?.unread_messages_count || 0;
  const messagesPath = company?.marketplace_metrics?.messages_path || '/succession/messages';

  const marketplaceMetrics = [
    // First item removed and replaced with SellerValueMetricContainer component
    {
      name: 'Active Buyers',
      value: String(activeBuyersValue),
      change: `${activeBuyersChange} this week`,
      icon: UserGroupIcon,
      color: 'text-succession-success',
      bgColor: 'bg-succession-success-bg',
      changeColor: 'text-succession-success'
    },
    {
      name: 'Your Followers',
      value: String(followersCount),
      change: '',
      icon: BuildingOfficeIcon,
      color: 'text-succession-secondary',
      bgColor: 'bg-succession-secondary-10',
      changeColor: 'text-succession-accent'
    },
    {
      name: 'Your Messages',
      value: String(unreadMessagesCount),
      change: '',
      action: { label: 'View Messages', href: messagesPath },
      icon: CurrencyDollarIcon,
      color: 'text-succession-warning',
      bgColor: 'bg-succession-warning-bg',
      changeColor: 'text-succession-success'
    }
  ];
  
  const showRecentActivity = Array.isArray(company?.feature_types) && company.feature_types.includes('dashboard_recent_activity');

  useEffect(() => {
    const user = cuData?.getCurrentUser || current_user;
    const showIntake = !!user && shouldShowUnifiedIntake(user);
    setShowIntakeOverlay(!!showIntake);
    try {
      if (showIntake) {
        document.documentElement.classList.add('intake-overlay-active');
      } else {
        document.documentElement.classList.remove('intake-overlay-active');
      }
    } catch (_) {}

    // Handle optional tutorial pop once; if present, switch to Value tab first, then open when ready
    try {
      const url = new URL(window.location.href);
      const tutorial = url.searchParams.get('showTutorial');
      if (tutorial === '1') {
        setActiveTab('value');
        setPendingTutorial(true);
        // Defer opening the tutorial until after the Value tab mounts and paints
        try {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setShowTutorial((prev) => prev || true);
              setPendingTutorial(false);
            });
          });
        } catch (_) {}
        url.searchParams.delete('showTutorial');
        window.history.replaceState({}, '', url.toString());
      }
    } catch (_) {}
  }, [cuData, current_user]);

  // Toggle a global class so layout/header can react (hide sidebar/search/notifications)
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    if (showIntakeOverlay) {
      root.classList.add('intake-overlay-active');
      body.classList.add('intake-overlay-active');
    } else {
      root.classList.remove('intake-overlay-active');
      body.classList.remove('intake-overlay-active');
    }
    return () => { root.classList.remove('intake-overlay-active'); body.classList.remove('intake-overlay-active'); };
  }, [showIntakeOverlay]);

  // Ensure sidebar is hidden while overlay is active even if CSS specificity conflicts
  useEffect(() => {
    const sidebar = document.getElementById('sidebar');
    const mobileHeader = document.querySelector('.mobile-header');
    if (showIntakeOverlay) {
      if (sidebar) sidebar.style.setProperty('display', 'none', 'important');
      if (mobileHeader) mobileHeader.style.setProperty('display', 'none', 'important');
    } else {
      if (sidebar) sidebar.style.removeProperty('display');
      if (mobileHeader) mobileHeader.style.removeProperty('display');
    }
    return () => {
      if (sidebar) sidebar.style.removeProperty('display');
      if (mobileHeader) mobileHeader.style.removeProperty('display');
    };
  }, [showIntakeOverlay]);

  // Nudge chart libraries to recalc sizes when tutorial overlays the Value tab
  useEffect(() => {
    if (activeTab === 'value' && showTutorial) {
      try { setTimeout(() => window.dispatchEvent(new Event('resize')), 50); } catch (_) {}
    }
  }, [activeTab, showTutorial]);

  useEffect(() => {
    if (activeTab === 'value' && !showTutorial) {
      try { setTimeout(() => window.dispatchEvent(new Event('resize')), 50); } catch (_) {}
    }
  }, [activeTab, showTutorial]);

  useEffect(() => {
    if (activeTab === 'value') {
      // Nudge charts to measure when the tab first becomes active
      try { setTimeout(() => window.dispatchEvent(new Event('resize')), 30); } catch (_) {}
    }
  }, [activeTab]);

  // Fallback: if pendingTutorial is set and onReady hasn't opened the tutorial within 700ms,
  // open it anyway (we still suppress the prompt so charts show under the overlay).
  useEffect(() => {
    if (activeTab === 'value' && pendingTutorial && !showTutorial) {
      const t = setTimeout(() => {
        if (pendingTutorial && !showTutorial) {
          setShowTutorial(true);
          setPendingTutorial(false);
        }
      }, 700);
      return () => clearTimeout(t);
    }
  }, [activeTab, pendingTutorial, showTutorial]);

  return (
    <div className="flex h-screen bg-succession-bg-secondary transition-colors duration-300">
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <main className={`flex-1 overflow-x-hidden overflow-y-auto bg-succession-bg-secondary p-6 transition-colors duration-300 ${showIntakeOverlay ? 'filter blur-sm' : ''}`}>
          <Container maxWidth="xl">
            <div className="px-1 pb-2">
              <h1 className="text-xl font-semibold text-succession-text-primary">Welcome, {current_user?.name || 'User'}</h1>
            </div>
            {/* Tabs */}
            {(() => {
              const user = cuData?.getCurrentUser || current_user;
              const hasDashboardFlag = (
                (Array.isArray(user?.features) && user.features.includes('triggering_event_dashboard')) ||
                (Array.isArray(user?.company?.feature_types) && user.company.feature_types.includes('triggering_event_dashboard'))
              );
              const lockValueTab = shouldShowAssessmentPrompt(user);
              return (
                <div className="mb-4 border-b border-succession-border">
                  <nav className="-mb-px flex gap-4" aria-label="Tabs">
                    <button
                      className={`whitespace-nowrap py-2 px-4 border-b-2 text-sm font-medium ${activeTab === 'marketplace' ? 'border-succession-primary text-succession-primary' : 'border-transparent text-succession-text-secondary hover:text-succession-text-primary'}`}
                      onClick={() => setActiveTab('marketplace')}
                    >
                      Marketplace
                    </button>
                    <button
                      className={`whitespace-nowrap py-2 px-4 border-b-2 text-sm font-medium ${activeTab === 'value' ? 'border-succession-primary text-succession-primary' : 'border-transparent text-succession-text-secondary hover:text-succession-text-primary'} ${!hasDashboardFlag ? 'opacity-50 cursor-not-allowed' : ''}`}
                      onClick={() => { if (hasDashboardFlag) setActiveTab('value'); }}
                      aria-disabled={!hasDashboardFlag}
                    >
                      Value Acceleration {lockValueTab ? <span className="ml-1 align-middle text-succession-text-muted" aria-hidden="true">🔒</span> : null}
                    </button>
                  </nav>
                </div>
              );
            })()}

            <Grid container spacing={3}>
              {activeTab === 'value' ? (
                <>
                  <Grid item xs={12}>
                    <div className="bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-colors duration-300 p-4">
                      <ValueAccelerationContainer
                        key={`va-${vaRefreshToken}-${showTutorial ? 't' : 'live'}`}
                        suppressPrompt={pendingTutorial || showTutorial}
                        onReady={() => { if (pendingTutorial) { setShowTutorial(true); setPendingTutorial(false); } }}
                      />
                    </div>
                  </Grid>
                </>
              ) : (
                <>
              {/* Seller Value Metric Card (first card) */}
              <Grid item xs={12} sm={6} md={3}>
                <ErrorBoundary>
                  <SellerValueMetricContainer />
                </ErrorBoundary>
              </Grid>
              
              {/* Remaining Marketplace Metrics Cards */}
              {marketplaceMetrics.map((metric) => (
                <Grid item xs={12} sm={6} md={3} key={metric.name}>
                  <div className="bg-succession-bg-primary overflow-hidden shadow-succession-md rounded-lg h-full transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform border border-succession-border">
                    <div className="px-4 py-5 sm:p-6">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 rounded-md p-3 ${metric.bgColor} transition-colors duration-300`}>
                          <metric.icon className={`h-6 w-6 ${metric.color} transition-colors duration-300`} aria-hidden="true" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-succession-text-secondary transition-colors duration-300">
                            {metric.name}
                          </p>
                          <p className="text-lg font-semibold text-succession-text-primary transition-colors duration-300">
                            {metric.value}
                          </p>
                          {metric.change ? (
                            <p className={`text-sm ${metric.changeColor} transition-colors duration-300`}>
                              {metric.change}
                            </p>
                          ) : null}
                          {metric.action ? (
                            <p className="text-sm mt-1">
                              <a href={metric.action.href} className="text-succession-primary hover:text-succession-primary-hover transition-colors duration-300">
                                {metric.action.label}
                              </a>
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </Grid>
              ))}

              {/* Value Acceleration Prompt Tiles - stack left under xs=12/md=4 */}
              {(() => {
                const user = cuData?.getCurrentUser || current_user;
                const enabledForUser = Array.isArray(user?.features) && user.features.includes('triggering_event_dashboard');
                const enabledForCompany = Array.isArray(user?.company?.feature_types) && user.company.feature_types.includes('triggering_event_dashboard');
                const gate = (enabledForUser || enabledForCompany) && shouldShowAssessmentPrompt(user);
                return gate;
              })() && (
                <Grid item xs={12} md={4} className="flex">
                  <div className="flex-1 flex flex-col gap-3">
                    <div className="flex-1">
                      <AssessmentCtaTile placement="dashboard_marketplace" />
                    </div>
                    <div className="flex-1">
                      <ActionPlanCtaTile state="locked" onClick={() => window.location.href = '/succession/plans'} />
                    </div>
                  </div>
                </Grid>
              )}

              {/* Main Metrics with enhanced styling */}
              <Grid item xs={12} md={8} className="flex">
                <div className="bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-colors duration-300 h-full w-full flex flex-col min-h-full">
                  <ErrorBoundary>
                    <ValuationTileContainer current_user={current_user} />
                  </ErrorBoundary>
                </div>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <div className="bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-colors duration-300">
                  <ErrorBoundary>
                    <PlanningProgress 
                      title="Planning Progress"
                      steps={getPlanningSteps()}
                      loading={loading}
                    />
                  </ErrorBoundary>
                </div>
              </Grid>
              
              {/* BCP, Catastrophe, and Succession Planning Tiles with enhanced styling */}
              <Grid item xs={12} md={4}>
                <div className="bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform">
                  <ErrorBoundary>
                    <BcpTileContainer />
                  </ErrorBoundary>
                </div>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <div className="bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform">
                  <ErrorBoundary>
                    <CatastropheTileContainer />
                  </ErrorBoundary>
                </div>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <div className="bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-all duration-300 hover:shadow-succession-lg hover:scale-105 transform">
                  <ErrorBoundary>
                    <SuccessionPlanningTileContainer />
                  </ErrorBoundary>
                </div>
              </Grid>

              {/* Deal Room with gradient background */}
              <Grid item xs={12} md={12}>
                <div className="bg-gradient-to-r from-succession-primary-10 to-succession-secondary-10 rounded-lg shadow-succession-md border border-succession-border transition-colors duration-300">
                  <ErrorBoundary>
                    <DealRoom />
                  </ErrorBoundary>
                </div>
              </Grid>

              {/* Activity Stream with enhanced styling (behind feature flag) */}
              {showRecentActivity && (
                <Grid item xs={12}>
                  <div className="bg-succession-bg-primary rounded-lg shadow-succession-md border border-succession-border transition-colors duration-300">
                    <ErrorBoundary>
                      <ActivityStream 
                        activities={[
                          {
                            id: 1,
                            type: 'valuation',
                            content: 'Practice valuation completed',
                            timestamp: '2 hours ago'
                          },
                          {
                            id: 2,
                            type: 'deal',
                            content: 'New potential successor identified',
                            timestamp: '1 day ago'
                          },
                          {
                            id: 3,
                            type: 'listing',
                            content: 'Succession questionnaire started',
                            timestamp: '2 days ago'
                          }
                        ]}
                      />
                    </ErrorBoundary>
                  </div>
                </Grid>
              )}
                </>
              )}
            </Grid>
          </Container>
        </main>

        <DashboardTutorial
          isOpen={showTutorial}
          onClose={() => { setShowTutorial(false); setVaRefreshToken((t) => t + 1); }}
          onComplete={() => { setShowTutorial(false); setVaRefreshToken((t) => t + 1); }}
        />

        {showIntakeOverlay && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
            <div className="max-w-2xl mx-auto text-center p-8">
              <div className="space-y-6">
                <h2 className="text-3xl font-bold text-succession-text-primary">Take the first step toward securing your future</h2>
                <p className="text-lg text-succession-text-secondary">Start your Readiness Assessment to unlock your Value Acceleration dashboard.</p>
                <div className="flex items-center justify-center gap-3">
                  <button className="inline-flex items-center gap-3 px-6 py-3 bg-succession-primary text-white rounded-xl font-semibold hover:bg-succession-primary/90" onClick={() => window.location.href = '/succession/plans'}>Start Succession Plan</button>
                  <button className="px-6 py-3 rounded-xl border border-succession-border text-succession-text-secondary hover:bg-white" onClick={() => { snoozeUnifiedIntakeForSession(); setShowIntakeOverlay(false); }}>Maybe later</button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <SuccessionDrawerWithListener />
      
      {/* Agent Widget Renderer - renders spawned widgets */}
      <AgentWidgetRenderer />
      
      {/* Smart Agent Trigger System - handles contextual triggering */}
      <AgentTriggerSystem />
    </div>
  );
};

// Main component wrapper with theme provider and agent system
const SuccessionDashboard = ({ current_user, company, features, selected_user_name, agent_insights, agent_recommendations, show_agent_insights }) => {
  // Merge features into company object for consistent access
  const companyWithFeatures = { ...company, features: features || [] };
  
  return (
    <ApolloProvider client={client}>
      <SuccessionThemeProvider company={companyWithFeatures} user={current_user}>
        <AgentProvider>
          <SuccessionDashboardContent 
            current_user={current_user} 
            company={companyWithFeatures} 
            selected_user_name={selected_user_name}
            agent_insights={agent_insights}
            agent_recommendations={agent_recommendations}
            show_agent_insights={show_agent_insights}
          />
        </AgentProvider>
      </SuccessionThemeProvider>
    </ApolloProvider>
  );
};

export default SuccessionDashboard; 